"use client";

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, Loader2, AlertCircle } from "lucide-react";
import { getBaseUrl } from "../../config/apiConfig";

function AdminAccountAction() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const { t } = useTranslation();

  const action = searchParams.get("action"); // "approve" or "reject"
  const userId = searchParams.get("userId");
  const email = searchParams.get("email");
  const firstName = searchParams.get("firstName");
  const lastName = searchParams.get("lastName");
  const username = searchParams.get("username");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!action || !userId || !token) {
      setStatus("error");
      setErrorMessage(t("adminAction.invalidLink"));
      return;
    }

    const endpoint = action === "approve" ? "approve-account" : "reject-account";

    const params = new URLSearchParams({ userId, token });
    if (email) params.set("email", email);
    if (firstName) params.set("firstName", firstName);
    if (lastName) params.set("lastName", lastName);
    if (username) params.set("username", username);

    fetch(`${getBaseUrl()}/api/user/${endpoint}?${params.toString()}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || data.message || "Request failed");
        }
        setResult(data);
        setStatus("success");
      })
      .catch((err) => {
        console.error(`Admin action failed:`, err);
        setErrorMessage(err.message || t("adminAction.unexpectedError"));
        setStatus("error");
      });
  }, [action, userId, email, firstName, lastName, username, token, t]);

  const isApprove = action === "approve";

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white/95 dark:bg-slate-800/95 backdrop-blur-[20px] rounded-[2.5rem] shadow-[0_24px_80px_rgba(0,0,0,0.15)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.4)] border border-white/30 dark:border-slate-700/30 p-8 sm:p-12">
        {status === "loading" && (
          <div className="text-center py-8">
            <Loader2 size={48} className="animate-spin text-[#0f7699] dark:text-[#4fc3f7] mx-auto mb-6" />
            <h2 className="text-xl font-black text-slate-700 dark:text-slate-200 mb-2">
              {isApprove ? t("adminAction.approving") : t("adminAction.rejecting")}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {t("adminAction.pleaseWait")}
            </p>
          </div>
        )}

        {status === "success" && isApprove && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-500 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-green-500/20">
              <CheckCircle2 size={40} className="text-white" />
            </div>
            <h2 className="text-2xl font-black text-green-600 dark:text-green-400 mb-4 tracking-tight">
              {t("adminAction.approved")}
            </h2>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-700/30 rounded-2xl p-6 text-left mb-6">
              <div className="space-y-2 text-sm">
                {result?.user?.firstName && (
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold">{t("adminAction.name")}:</span> {result.user.firstName} {result.user.lastName}
                  </p>
                )}
                {result?.user?.email && (
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold">{t("adminAction.email")}:</span> {result.user.email}
                  </p>
                )}
                {result?.user?.company && (
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold">{t("adminAction.company")}:</span> {result.user.company}
                  </p>
                )}
              </div>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {t("adminAction.approvedInfo")}
            </p>
          </div>
        )}

        {status === "success" && !isApprove && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
              <XCircle size={40} className="text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-black text-red-600 dark:text-red-400 mb-4 tracking-tight">
              {t("adminAction.rejected")}
            </h2>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-700/30 rounded-2xl p-6 text-left mb-6">
              <div className="space-y-2 text-sm">
                {result?.user?.firstName && (
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold">{t("adminAction.name")}:</span> {result.user.firstName} {result.user.lastName}
                  </p>
                )}
                {result?.user?.email && (
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold">{t("adminAction.email")}:</span> {result.user.email}
                  </p>
                )}
              </div>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {t("adminAction.rejectedInfo")}
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 rounded-[2rem] flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={40} className="text-amber-600 dark:text-amber-400" />
            </div>
            <h2 className="text-2xl font-black text-amber-600 dark:text-amber-400 mb-4 tracking-tight">
              {t("adminAction.errorTitle")}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
              {errorMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminAccountAction;
